import React, { useState, useEffect } from "react";
import propTypes from "prop-types";
import Select, {
  components,
  createFilter as rsCreateFilter
} from "react-select";
import Icon from "../icons/Icon";
import classnames from "classnames";
import Label from "../form-inputs/Label";
import { isObject } from "util";

import "../form-inputs/Dropdown.scss";
import { createDataAttributes } from "../../utilities";

// React Select
// https://react-select.com

const createFilter = filter => {
  if (isObject(filter)) {
    return rsCreateFilter(filter);
  }
  return filter;
};

const DropdownIndicator = React.memo(props => {
  return (
    <components.DropdownIndicator {...props}>
      <Icon icon="fasCaretDown" />
    </components.DropdownIndicator>
  );
});

DropdownIndicator.displayName = "DropdownIndicator";

const IndicatorSeparator = ({ innerProps }) => {
  return (
    <span className="uikit__dropdown__indicator-separator" {...innerProps} />
  );
};

const DropdownNonForm = props => {
  const {
    id,
    name,
    label,
    labelElement,
    options,
    placeholder,
    filter,
    value,
    className,
    disabled,
    onChange,
    onBlur,
    size,
    isClearable,
    isSearchable,
    onSearch,
    hideIndicatorUntilSearch,
    hideLabel,
    noLabel,
    compactIndicator,
    dataAttrib,
    ...rest
  } = props;

  const [searchText, setSearchText] = useState("");
  const [opts, setOpts] = useState(options);

  useEffect(() => {
    if (options.length) {
      if (!isObject(options[0])) {
        const newOpts = options.map(option => ({
          label: option,
          value: option
        }));
        setOpts(newOpts);
      } else {
        setOpts(options);
      }
    }
  }, [options]);

  const classes = classnames(
    "uikit__dropdown__size__" + size,
    "uikit__dropdown-wrapper",
    {
      "uikit__dropdown__compact-indicator": compactIndicator,
      "uikit__hide-label": hideLabel
    },
    className
  );

  const inputClasses = classnames("uikit__dropdown");
  const handleChange = selection => {
    if (onChange) onChange(selection);
  };

  const handleBlur = selection => {
    if (onBlur) onBlur(selection);
  };

  const handleSearchableChange = inputValue => {
    if (isSearchable && onSearch) {
      if (inputValue.length > 0) {
        setSearchText(inputValue);
        onSearch(inputValue);
      }
    }
  };

  /* hide dropdown indicator */
  const indicators =
    hideIndicatorUntilSearch && isSearchable && searchText.length === 0
      ? { DropdownIndicator: () => null, IndicatorSeparator: () => null }
      : { DropdownIndicator, IndicatorSeparator };

  return (
    <div className={classes} {...createDataAttributes(dataAttrib)}>
      {!noLabel && (
        <Label htmlFor={id} label={label} labelElement={labelElement} />
      )}
      <Select
        inputId={id}
        name={name}
        options={opts}
        value={
          opts.length
            ? opts.find(o => {
                return (
                  o.value === value ||
                  (isObject(value) && o.value === value.value)
                );
              })
            : value === undefined || isObject(value)
            ? value
            : { value, label: value }
        }
        placeholder={placeholder}
        className={inputClasses}
        filterOption={createFilter(filter)}
        onChange={handleChange}
        onBlur={handleBlur}
        isSearchable={isSearchable}
        onInputChange={handleSearchableChange}
        classNamePrefix="uikit__dropdown"
        isClearable={isClearable}
        components={indicators}
        isDisabled={disabled}
        {...rest}
      />
    </div>
  );
};

DropdownNonForm.propTypes = {
  //global form props
  className: propTypes.string,
  disabled: propTypes.bool,
  id: propTypes.string.isRequired,
  label: propTypes.oneOfType([
    propTypes.string,
    propTypes.object,
    propTypes.element,
    propTypes.func
  ]),
  labelElement: propTypes.element,
  name: propTypes.string.isRequired,
  onBlur: propTypes.func,
  onChange: propTypes.func,
  hideLabel: propTypes.bool,
  noLabel: propTypes.bool,

  /** Filter config object or function to use for filtering options by text input
   * Default settings:
   * - ignoreCase: true,
   * - ignoreAccents:  true,
   * - stringify: option => `${option.label} ${option.value}`,
   * - trim: true,
   * - matchFrom: "any"
   *
   * Customer filter function needs to have the signature:
   * filter = (Option, String) => Bool
   */
  onSearch: propTypes.func,
  size: propTypes.oneOf([
    "auto",
    "full",
    "tiny",
    "sm",
    "md",
    "lg",
    "25p",
    "33p",
    "50p",
    "fitToContent",
    "fill"
  ]),
  filter: propTypes.oneOfType([
    propTypes.func,
    propTypes.shape({
      ignoreCase: propTypes.bool,
      ignoreAccents: propTypes.bool,
      stringify: propTypes.func,
      trim: propTypes.bool,
      matchFrom: propTypes.oneOf(["any", "start"])
    })
  ]),
  isClearable: propTypes.bool,
  isSearchable: propTypes.bool,
  hideIndicatorUntilSearch: propTypes.bool,
  value: propTypes.oneOfType([
    propTypes.string,
    propTypes.number,
    propTypes.shape({
      value: propTypes.oneOfType([propTypes.string, propTypes.number]),
      label: propTypes.oneOfType([propTypes.string, propTypes.number])
    })
  ]),
  options: propTypes.arrayOf(
    propTypes.oneOfType([
      propTypes.shape({
        value: propTypes.oneOfType([propTypes.string, propTypes.number]),
        label: propTypes.oneOfType([
          propTypes.string,
          propTypes.object,
          propTypes.element
        ])
      }),
      propTypes.string,
      propTypes.number
    ])
  ).isRequired,
  placeholder: propTypes.string,
  compactIndicator: propTypes.bool,
  dataAttrib: propTypes.arrayOf(Object)
};

DropdownNonForm.defaultProps = {
  isSearchable: false,
  isClearable: true,
  hideIndicatorUntilSearch: false,
  placeholder: "Select One...",
  size: "md"
};

export default DropdownNonForm;
